import { Injectable } from '@angular/core';
import { TemplateContext, ReportTemplate } from '../models/reportTemplate.model';
import { CustomField } from '../models/customField.model';
import { TemplateMapperRegistry, FilterOption, EmailTemplate } from './template-mappers';

@Injectable({
  providedIn: 'root'
})
export class GenericTemplateService {

  constructor(
    private mapperRegistry: TemplateMapperRegistry
  ) { }

  /**
   * Check if we can handle the given entity type
   */
  canHandle(entityType: string): boolean {
    return this.mapperRegistry.hasMapper(entityType);
  }

  /**
   * Get available filters for an entity
   */
  getAvailableFilters(entity: unknown, entityType: string, template?: ReportTemplate): FilterOption[] {
    const mapper = this.mapperRegistry.getMapper(entityType);
    if (!mapper) {
      return [];
    }

    return mapper.analyzeFilters(entity, template);
  }

  /**
   * Apply filters to entity data
   */
  applyFilters(entity: unknown, entityType: string, filters: Record<string, (number | string)[]>): unknown[] {
    const mapper = this.mapperRegistry.getMapper(entityType);
    if (!mapper) {
      return [];
    }

    return mapper.applyFilters(entity, filters);
  }

  /**
   * Get default user inputs for a template
   */
  getDefaultUserInputs(entityType: string): Record<string, string> {
    const mapper = this.mapperRegistry.getMapper(entityType);
    if (!mapper) {
      return {};
    }

    return mapper.getDefaultUserInputs();
  }

  /**
   * Map entity to template context
   */
  async mapToTemplateContext(
    entity: unknown,
    entityType: string,
    template: ReportTemplate,
    userInputs: Record<string, string | boolean>,
    filteredData?: unknown[]
  ): Promise<TemplateContext> {
    const mapper = this.mapperRegistry.getMapper(entityType);
    if (!mapper) {
      throw new Error(`No mapper found for entity type: ${entityType}`);
    }

    return await mapper.mapToTemplateContext(entity, template, userInputs, filteredData);
  }

  updateCascadingFilters(entity: unknown, entityType: string, filters: FilterOption[], changedFilterPath: string): void {
    const mapper = this.mapperRegistry.getMapper(entityType);
    if (!mapper) return;
    mapper.updateCascadingFilters(entity, filters, changedFilterPath);
  }

  getEmailTemplate(entity: unknown, entityType: string, customFields?: CustomField[]): EmailTemplate | null {
    const mapper = this.mapperRegistry.getMapper(entityType);
    if (!mapper) {
      return null;
    }

    return mapper.getEmailTemplate(entity, customFields);
  }
}
